import { FormEvent, useState } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { AuthLayout, Fehlermeldung } from './AuthLayout';
import { sicheresZiel } from '../../utils/sicheresZiel';

export default function MfaPage() {
  const { status, mfa, mfaBestaetigen, mfaErneutSenden } = useAuth();
  const navigate = useNavigate();
  const ort = useLocation();
  const weiter = sicheresZiel(new URLSearchParams(ort.search).get('weiter'));

  const [code, setCode] = useState('');
  const [vertrauen, setVertrauen] = useState(false);
  const [fehler, setFehler] = useState<unknown>(null);
  const [laeuft, setLaeuft] = useState(false);
  const [erneutGesendet, setErneutGesendet] = useState(false);

  if (status === 'angemeldet') return <Navigate to={weiter} replace />;
  // Ohne ausstehende Bestaetigung hat diese Seite keinen Zustand, an den sie
  // anknuepfen koennte -- etwa nach einem Neuladen mitten im Ablauf.
  if (status !== 'mfa_ausstehend' || !mfa) return <Navigate to="/login" replace />;

  async function absenden(e: FormEvent) {
    e.preventDefault();
    setFehler(null);
    setLaeuft(true);
    try {
      await mfaBestaetigen(code, vertrauen, navigator.userAgent.slice(0, 100));
      navigate(weiter, { replace: true });
    } catch (err) {
      setFehler(err);
    } finally {
      setLaeuft(false);
    }
  }

  async function nochmal() {
    setFehler(null);
    try {
      await mfaErneutSenden();
      setErneutGesendet(true);
    } catch (err) {
      setFehler(err);
    }
  }

  return (
    <AuthLayout
      titel="Bestätigungscode eingeben"
      untertitel={
        <>
          Wir haben einen Code an <strong>{mfa.zielEmailMaskiert}</strong> gesendet.
          Er gilt 10&nbsp;Minuten.
        </>
      }
    >
      <Fehlermeldung fehler={fehler} />

      <form onSubmit={absenden} className="space-y-4">
        <div>
          <label htmlFor="code" className="block text-sm font-medium text-gray-700">
            Code
          </label>
          <input
            id="code"
            inputMode="numeric"
            autoComplete="one-time-code"
            pattern="[0-9]{6}"
            maxLength={6}
            required
            autoFocus
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
            className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-center text-2xl tracking-[0.5em] focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
          />
        </div>

        <label className="flex items-center gap-2 text-sm text-gray-700">
          <input
            type="checkbox"
            checked={vertrauen}
            onChange={(e) => setVertrauen(e.target.checked)}
            className="rounded border-gray-300"
          />
          Diesem Gerät 30 Tage vertrauen
        </label>

        <button
          type="submit"
          disabled={laeuft || code.length !== 6}
          className="flex w-full items-center justify-center rounded-md bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-60"
        >
          {laeuft && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          Bestätigen
        </button>

        <div className="text-center text-sm">
          {erneutGesendet ? (
            <span className="text-gray-500">
              Neuer Code gesendet. Der vorherige gilt nicht mehr.
            </span>
          ) : (
            <button type="button" onClick={nochmal} className="text-gray-600 hover:underline">
              Code nicht erhalten? Erneut senden
            </button>
          )}
        </div>
      </form>
    </AuthLayout>
  );
}
